import {
  type Theme,
  alpha,
  darken,
  decomposeColor,
  getContrastRatio,
  lighten,
  recomposeColor,
} from '@mui/material/styles';
import type { RhfSelectOption } from '@core/components';
import { adjustUntilMinContrast } from '@core/utils/color';
import type { ProductionItem } from '../../api/productionItems';
import { VestmentCategory } from '../enum/vestmentCategory';
import { getCategory } from '../enum/vestmentType';
import { getFullName } from './customer';

const MIN_TEXT_CONTRAST = 4.5;

export type ProductionCardColors = {
  color: string;
  secondaryColor: string;
  backgroundColor: string;
  borderColor?: string;
  hoverBorderColor?: string;
};

function compositeOver(
  foreground: string,
  background: string,
  amount: number
): string {
  const fg = decomposeColor(foreground);
  const bg = decomposeColor(background);
  const channel = (index: number) =>
    fg.values[index] * amount + bg.values[index] * (1 - amount);

  return recomposeColor({
    type: 'rgb',
    values: [channel(0), channel(1), channel(2)],
  });
}

function readableText(seed: string, background: string): string {
  return adjustUntilMinContrast(seed, background, MIN_TEXT_CONTRAST);
}

function quieterText(color: string, background: string): string {
  const lightened = lighten(color, 0.12);
  const darkened = darken(color, 0.12);
  const closerToFill =
    getContrastRatio(lightened, background) <=
    getContrastRatio(darkened, background)
      ? lightened
      : darkened;

  return readableText(closerToFill, background);
}

export function getProductionCardColors(
  theme: Theme,
  hexCode?: string | null
): ProductionCardColors {
  const main = hexCode?.trim();
  const isDark = theme.palette.mode === 'dark';

  if (!main) {
    if (isDark) {
      return {
        color: 'primary.contrastText',
        secondaryColor: 'primary.contrastText',
        backgroundColor: 'primary.main',
      };
    }
    return {
      color: 'text.primary',
      secondaryColor: 'text.secondary',
      backgroundColor: 'background.paper',
      borderColor: 'primary.main',
      hoverBorderColor: 'primary.main',
    };
  }

  const hasStrongBlackContrast =
    getContrastRatio(main, '#000') >= MIN_TEXT_CONTRAST;
  let backgroundColor: string;
  let contrastBackground: string;

  if (isDark && getContrastRatio(main, '#fff') >= MIN_TEXT_CONTRAST) {
    backgroundColor = lighten(main, 0.75);
    contrastBackground = backgroundColor;
  } else if (hasStrongBlackContrast) {
    backgroundColor = main;
    contrastBackground = main;
  } else {
    backgroundColor = alpha(main, 0.25);
    contrastBackground = compositeOver(
      main,
      theme.palette.background.paper,
      0.25
    );
  }

  const backgroundIsLight =
    getContrastRatio(contrastBackground, '#000') >= MIN_TEXT_CONTRAST;
  const color = readableText(
    backgroundIsLight ? darken(main, 0.45) : lighten(main, 0.45),
    contrastBackground
  );

  return {
    color,
    secondaryColor: quieterText(color, contrastBackground),
    backgroundColor,
    borderColor: !isDark ? darken(main, 0.25) : undefined,
    hoverBorderColor: !isDark ? darken(main, 0.4) : undefined,
  };
}

export type ProductionFilters = {
  orderId: string;
  category: VestmentCategory | '';
};

export const EMPTY_PRODUCTION_FILTERS: ProductionFilters = {
  orderId: '',
  category: '',
};

export function getProductionOrderOptions(
  items: ProductionItem[]
): RhfSelectOption[] {
  const orders = new Map<number, ProductionItem['order']>();
  for (const item of items) {
    orders.set(item.order.id, item.order);
  }
  return Array.from(orders.values())
    .sort((a, b) => a.id - b.id)
    .map((order) => ({
      value: String(order.id),
      label: `${getFullName(order.customer)} (#${order.id})`,
    }));
}

export function countActiveProductionFilters(
  filters: ProductionFilters
): number {
  let count = 0;
  if (filters.orderId) count += 1;
  if (filters.category) count += 1;
  return count;
}

export function getProductionItemSearchHaystack(item: ProductionItem): string {
  return [
    getFullName(item.order.customer),
    item.vestment?.name,
    item.design?.code,
  ]
    .filter(Boolean)
    .join(' ');
}

export function matchProductionFilters(
  item: ProductionItem,
  filters: ProductionFilters
): boolean {
  if (filters.orderId && String(item.order.id) !== filters.orderId) {
    return false;
  }
  if (filters.category) {
    const type = item.vestment?.type;
    if (!type || getCategory(type) !== filters.category) {
      return false;
    }
  }
  return true;
}
